import { AppAccessType } from "@fitness-app/data-models/entities/Automation";
import { TraineeStatus } from "@fitness-app/data-models/entities/Trainee";

import { type StoreDependencies } from "../../../index";

type Db = StoreDependencies["db"];

export type TraineesWithoutPlanScope =
  /** Trainer view: only clients assigned to that member, full access only. */
  | { type: "assignedTrainer"; assignedTrainerId: string }
  /** Administration view: whole account, every access type (TRE-1841). */
  | { type: "tenant" };

const traineesWithoutPlanSelect =
  "id, userId, firstName, lastName, email, type, createdAt, status, assignedTrainerId, hasActiveNutrition, hasActiveTrainingProgram, activeTrainingProgramId, activeMealsPlanId, activePlan:activeTrainingProgramId(name), activeMealsPlan:activeMealsPlanId(name)";

/**
 * Cap on rows pulled into the dashboard table. The tenant-wide list sits around 150 rows today,
 * so the limit is only a guard against the list growing into an unbounded render — `count` tells
 * the caller when it actually truncated.
 */
export const TRAINEES_WITHOUT_PLAN_LIMIT = 500;

/** Active clients missing a training plan or a diet, narrowed to the caller's scope. */
export function buildTraineesWithoutPlanQuery(db: Db, scope: TraineesWithoutPlanScope) {
  const query = db
    .from("trainee")
    .select(traineesWithoutPlanSelect, { count: "exact" })
    .order("createdAt", { ascending: false })
    .eq("status", TraineeStatus.ACTIVE)
    .or("hasActiveNutrition.is.false,hasActiveTrainingProgram.is.false");

  if (scope.type === "assignedTrainer") {
    return query
      .eq("assignedTrainerId", scope.assignedTrainerId)
      .eq("type", AppAccessType.FullAccess)
      .limit(TRAINEES_WITHOUT_PLAN_LIMIT);
  }

  // Administration handles the whole account, so neither the assigned trainer nor the access type
  // narrows the list — limited-access clients wait for a plan or a diet just the same. The tenant
  // boundary comes from RLS (clients.read = ALL over the member's tenant).
  return query.limit(TRAINEES_WITHOUT_PLAN_LIMIT);
}
